import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import { connect } from '@reverbdotcom/commons/src/connect';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import React from 'react';
import {
  CoreProductReviews,
  reverb_search_ProductReviewsSearchRequest_Rating,
  reverb_search_ProductReviewsSearchRequest_Sort,
} from '../schemas/rql';
import { ProductReviewCardFragment } from './ProductReviewCard';
import RQLPagination from '@reverbdotcom/commons/src/components/rql_pagination';
import { getOffsetFromPage } from '../pagination_helpers';
import { withRouter, WithRouterProps } from 'react-router';
import { ProductReviewsFilterRow } from './ProductReviewsFilterRow';
import { ProductReviewFilterFragment } from './ProductReviewFilter';
import { ProductReviewsHeader } from './ProductReviewsHeader';
import LoadingOverlay from '../shared/loading_overlay';
import I18n from 'i18n-js';
import { ProductReviews } from './ProductReviews';
import { RCSearchInput } from '@reverbdotcom/cadence/components';
import { updateParams } from '@reverbdotcom/commons';
import { UpdateURLProvider, UpdateUrlArgs } from '@reverbdotcom/commons/src/components/with_update_url';
import { omit } from 'lodash';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ProductReviewInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';
import RouterHistory from '@reverbdotcom/commons/src/cms/lib/router_history';
import { useURL } from '@reverbdotcom/commons/src/url_context';

interface ExternalProps {
  cspId: string;
  trackingDisplayStyle: 'product' | 'csp';
}

type Props =  WithRouterProps & ChildProps<ExternalProps, CoreProductReviews.Query, CoreProductReviews.Variables>;

const RATINGS = {
  '5': reverb_search_ProductReviewsSearchRequest_Rating.FIVE,
  '4': reverb_search_ProductReviewsSearchRequest_Rating.FOUR,
  '3': reverb_search_ProductReviewsSearchRequest_Rating.THREE,
  '2': reverb_search_ProductReviewsSearchRequest_Rating.TWO,
  '1': reverb_search_ProductReviewsSearchRequest_Rating.ONE,
};

const SORT = {
  'created_at|desc': reverb_search_ProductReviewsSearchRequest_Sort.CREATED_AT_DESC,
  'product_review_votes_count|desc': reverb_search_ProductReviewsSearchRequest_Sort.VOTES_DESC,
  'rating|desc': reverb_search_ProductReviewsSearchRequest_Sort.RATING_DESC,
  'rating|asc': reverb_search_ProductReviewsSearchRequest_Sort.RATING_ASC,
  'updated_at|desc': reverb_search_ProductReviewsSearchRequest_Sort.UPDATED_AT_DESC,
};

const LIMIT = 5;
const COMPONENT_NAME = 'ProductReviewsLayout';

export function ProductReviewsLayout(props: Props) {
  const { data, location, trackingDisplayStyle, cspId } = props;
  const [searchDisplayValue, setSearchDisplayValue] = React.useState(location?.query?.review_query || '');
  const url = useURL();

  if (!data) {
    return null;
  }

  function refetchQuery() {
    const query = {
      ...location.query,
      review_query: searchDisplayValue,
    };

    trackEvent({
      eventName: MParticleEventName.ProductReviewAction,
      productReviewInteraction: ProductReviewInteraction.SearchReviews,
      displayStyle: trackingDisplayStyle,
      cspId: cspId,
      query: searchDisplayValue,
    });

    RouterHistory.push({
      query,
      pathname: location.pathname,
      hash: url.hash,
    });
  }

  function clearQuery() {
    trackEvent({
      eventName: MParticleEventName.ProductReviewAction,
      productReviewInteraction: ProductReviewInteraction.ClearSearchQuery,
      displayStyle: trackingDisplayStyle,
      cspId: cspId,
      query: searchDisplayValue,
    });

    setSearchDisplayValue('');
    if (props.location.query) {
      const query = omit(props.location.query, 'review_query');

      RouterHistory.push({
        query,
        pathname: props.location.pathname,
        hash: url.hash,
      });
    }
  }

  function updateUrl(args: UpdateUrlArgs) {
    const query = updateParams(props.location.query, args.newParams);

    return {
      pathname: args.location.pathname,
      query,
    };
  }

  return (
    <UpdateURLProvider updateUrl={updateUrl}>
      <section
        id="reviews-section"
        className="product-reviews-section"
      >
        <h1 className="size-250 weight-bold mb-2">
          {I18n.t('discovery.productReviews.header.title')}
        </h1>
        <div className="product-reviews__container">
          <div className="product-reviews__header">
            <ProductReviewsHeader
              stats={data.csp?.statsSearch?.stats}
              csp={data.csp}
              trackingDisplayStyle={trackingDisplayStyle}
            />
          </div>
          <div className="product-reviews__content">
            <ProductReviewsFilterRow
              loading={data.loading}
              reviewCount={data.csp?.statsSearch?.stats?.total}
              filters={data.csp?.productReviewSearch?.filters}
              searchInput={
                <div className="width-rem-30 mobile-fx-width-100">
                  <RCSearchInput
                    value={searchDisplayValue}
                    inputId="product-reviews-search"
                    onChange={(e) => setSearchDisplayValue(e.target.value)}
                    onSubmit={refetchQuery}
                    onClear={clearQuery}
                    placeholder={I18n.t('discovery.productReviews.searchInput.placeholder')}
                  />
                </div>
              }
            />
            <LoadingOverlay showOverlay={data.loading}>
              <ProductReviews
                reviews={data.csp?.productReviewSearch?.productReviews}
                reviewCount={data.csp?.statsSearch?.stats?.total}
                cspSlug={data.csp?.slug}
                cspId={data.csp?.id}
                searchQuery={location?.query?.review_query}
                trackingDisplayStyle={trackingDisplayStyle}
              />
            </LoadingOverlay>
            <RQLPagination
              limit={LIMIT}
              total={data.csp?.productReviewSearch?.total}
              anchor="product-reviews"
              offset={getOffsetFromPage(location?.query?.product_review_page, LIMIT)}
              pageParam="product_review_page"
              replaceHistory
              trackingData={{ displayStyle: COMPONENT_NAME }}
            />
          </div>
        </div>
      </section>
    </UpdateURLProvider>
  );
}

export const ProductReviewCSPFragment = gql`
  fragment ProductReviewCSPFields on CSP {
    _id
    id
    slug
    title
    myReview {
      id
      body
      title
      rating
    }
  }
`;

const withQuery = withGraphql<Props, CoreProductReviews.Query>(
  gql`
    query Core_Product_Reviews(
      $cspId: String
      $offset: Int
      $ratings: [reverb_search_ProductReviewsSearchRequest_Rating]
      $verified: Boolean
      $sort: reverb_search_ProductReviewsSearchRequest_Sort
      $fullTextQuery: String
    ) {
      csp(
        input: {
          id: $cspId
        }
      ) {
        _id
        ...ProductReviewCSPFields
        productReviewSearch(
          input: {
            sort: $sort
            ratings: $ratings
            verified: $verified
            limit: 5
            offset: $offset
            fullTextQuery: $fullTextQuery
          }
        ) {
          total
          productReviews {
            _id
            ...ProductReviewCardFields
          }
          filters {
            ...ProductReviewFilterFields
          }
        }
        statsSearch: productReviewSearch(
          input: {
            limit: 0
          }
        ) {
          stats {
            total
            averageRating
            ratingsDistribution {
              rating
              reviewCount
            }
          }
        }
      }
    }
    ${ProductReviewCardFragment}
    ${ProductReviewFilterFragment}
    ${ProductReviewCSPFragment}
  `,
  {
    skip: ownProps => !ownProps.cspId,
    options: (ownProps) => {
      return {
        variables: {
          cspId: ownProps.cspId,
          offset: getOffsetFromPage(ownProps.location?.query?.product_review_page, LIMIT),
          ratings: RATINGS[ownProps.location?.query?.rating],
          verified: ownProps.location?.query?.verified === 'true',
          sort: SORT[ownProps.location?.query?.review_sort],
          fullTextQuery: ownProps.location?.query?.review_query,
        },
      };
    },
  },
);

export default connect<ExternalProps>([
  withRouter,
  withQuery,
])(ProductReviewsLayout);
